/**
 * Le stock d'un article, ouvert depuis sa ligne de contre-étude, et la réservation.
 *
 * *Demande de Chris (`docs/dev_stock.docx`) : « quand un article figurant dans la table article
 * apparaît et que l'on clique sur groupe, cela ouvre le stock final de l'article à la date du jour
 * pour que l'assistante puisse dire je réserve ces articles du stock ».*
 *
 * ## Un panneau, et non une colonne de plus
 *
 * Le tableau du déboursé porte déjà une vingtaine de colonnes, et le stock ne concerne qu'une
 * poignée de lignes — celles dont le code est un article ET qui sont cochées « du groupe ». Une
 * colonne aurait ajouté autant de cases inertes qu'il y a d'autres lignes, ce qui est exactement
 * le raisonnement qui a laissé la colonne de provenance vide sur les lignes en heures.
 *
 * Le panneau s'ouvre au clic et **lit le stock à ce moment-là**. C'est délibéré : le magasin bouge
 * pendant qu'on travaille, et un stock porté par la lecture de la contre-étude serait déjà faux au
 * moment de l'afficher.
 *
 * ## Deux nombres, parce qu'il y a deux questions
 *
 * **En stock** répond « qu'y a-t-il au magasin ». **Disponible** répond « que puis-je encore
 * promettre » — c'est le stock moins ce qui est déjà réservé ailleurs et attend d'être préparé.
 * N'en montrer qu'un ferait promettre deux fois la même palette.
 *
 * ## Réserver plus que le disponible passe, et se voit
 *
 * La marchandise peut arriver d'ici la date demandée, et refuser obligerait l'assistante à
 * réserver faux ou à ne rien réserver. L'écran l'annonce après coup, puisque c'est le serveur qui
 * connaît le disponible au moment exact de l'écriture.
 */
import { useCallback, useEffect, useState } from "react";
import type { JSX } from "react";
import {
  chargerStockPourLaLigne,
  reserverDuStock,
  retirerReservation,
} from "./api";
import type { Reservation, StockPourLaLigne } from "./api";

function formaterDate(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getFullYear())}`;
}

/** Une date civile « 2026-09-15 » se lit « 15/09/2026 », sans passer par un objet date. */
function formaterDateCivile(iso: string): string {
  const [a, m, j] = iso.split("-");
  return `${j ?? ""}/${m ?? ""}/${a ?? ""}`;
}

/** Un décimal sans les zéros inutiles : « 12.000 » se lit « 12 ». */
function sansZeros(valeur: string): string {
  return valeur.includes(".") ? valeur.replace(/\.?0+$/, "") : valeur;
}

export function StockDeLaLigne({
  chantierId,
  code,
  quantiteDeLaLigne,
  modifiable,
  onFermer,
}: {
  readonly chantierId: string;
  readonly code: string;
  /** La quantité chiffrée sur la ligne : ce que l'assistante réserve le plus souvent. */
  readonly quantiteDeLaLigne: string | null;
  readonly modifiable: boolean;
  readonly onFermer: () => void;
}): JSX.Element {
  const [vue, setVue] = useState<StockPourLaLigne | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [avis, setAvis] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);

  const [quantite, setQuantite] = useState(
    quantiteDeLaLigne === null ? "" : sansZeros(quantiteDeLaLigne),
  );
  const [pourLe, setPourLe] = useState("");
  const [remarque, setRemarque] = useState("");
  /**
   * Le magasin regardé : `""` veut dire « tous » (078).
   *
   * *Demande de Chris, 15/09/2026 : « elle doit pouvoir choisir de voir les stock pour un magasin
   * ou pour tous ».*
   *
   * Il sert à deux choses qu'il ne faut pas confondre. **Regarder** tous les magasins a un sens :
   * on veut savoir ce que le groupe détient. **Réserver** dans « tous » n'en a aucun — le
   * magasinier doit savoir dans quel rayonnage aller —, donc le bouton réclame un magasin précis
   * tant que « tous » est choisi.
   *
   * Présélectionné quand il n'y en a qu'un : le sélecteur ne s'affiche même pas, il n'y a rien à
   * choisir.
   */
  const [magasin, setMagasin] = useState<string>("");

  const recharger = useCallback((): void => {
    chargerStockPourLaLigne(chantierId, code)
      .then((r) => {
        setVue(r);
        setErreur(null);
        // Un seul magasin : il n'y a rien à choisir, et le sélecteur ne s'affichera pas.
        setMagasin((actuel) => {
          if (r.sites.some((s) => s.id === actuel)) return actuel;
          return r.sites.length === 1 ? (r.sites[0]?.id ?? "") : "";
        });
      })
      .catch((x: unknown) => {
        setErreur(x instanceof Error ? x.message : String(x));
      });
  }, [chantierId, code]);

  useEffect(() => {
    recharger();
  }, [recharger]);

  const reserver = (): void => {
    setOccupe(true);
    setErreur(null);
    setAvis(null);
    reserverDuStock(chantierId, {
      articleCode: code,
      quantite: quantite.trim(),
      pourLe: pourLe.trim() === "" ? null : pourLe.trim(),
      remarque: remarque.trim() === "" ? null : remarque.trim(),
      siteId: magasin,
    })
      .then((r) => {
        /*
          Trois choses à dire, et aucune ne doit se taire.

          Le dépassement : réserver 12 quand 5 sont disponibles passe, et c'est une information —
          le magasin ne pourra pas tout sortir aujourd'hui.

          Le magasin prévenu : si aucun compte de magasin n'est actif, la réservation existe et
          personne n'a été invité à préparer. Annoncer « le magasin est prévenu » laisserait
          attendre une préparation que personne ne fera.
        */
        const demande = Number(r.reservation.quantite);
        const dispo = Number(r.disponibleAvant);
        const depasse = demande > dispo;
        setAvis(
          `${sansZeros(r.reservation.quantite)} ${r.reservation.unite ?? ""} réservés.` +
            (depasse
              ? ` ATTENTION : ${sansZeros(r.disponibleAvant)} seulement étaient disponibles —` +
                " le magasin ne pourra pas tout sortir tant que le stock n'est pas rentré."
              : "") +
            (r.magasinsPrevenus === 0
              ? " Mais AUCUN compte de magasin n'est actif : personne n'a été prévenu." +
                " Prévenez l'administrateur."
              : ` Le magasin est prévenu (${String(r.magasinsPrevenus)} compte${r.magasinsPrevenus > 1 ? "s" : ""}).`),
        );
        setRemarque("");
        recharger();
      })
      .catch((x: unknown) => {
        setErreur(x instanceof Error ? x.message : String(x));
      })
      .finally(() => {
        setOccupe(false);
      });
  };

  const retirer = (reservation: Reservation): void => {
    setOccupe(true);
    setErreur(null);
    setAvis(null);
    retirerReservation(chantierId, reservation.id)
      .then(() => {
        setAvis("Réservation retirée.");
        recharger();
      })
      .catch((x: unknown) => {
        setErreur(x instanceof Error ? x.message : String(x));
      })
      .finally(() => {
        setOccupe(false);
      });
  };

  /**
   * Les chiffres du magasin regardé, ou `null` quand on les regarde tous.
   *
   * `null` n'est pas « aucun magasin » : c'est « le total », et c'est ce que les champs affichent
   * alors. Un magasin choisi mais sans aucun mouvement rend une part à zéro, jamais `null` — le
   * serveur liste tous les magasins déclarés.
   */
  const part = vue === null || magasin === "" ? null : (vue.parSite.find((p) => p.siteId === magasin) ?? null);

  const negatif = vue !== null && Number(part === null ? vue.disponible : part.disponible) < 0;

  return (
    <div className="stock-ligne" role="dialog" aria-label={`Stock de l'article ${code}`}>
      <div className="stock-ligne__entete">
        <strong>{code}</strong>
        {vue !== null && <span>{vue.article.description}</span>}
        <button type="button" className="lien" onClick={onFermer}>
          Fermer
        </button>
      </div>

      {erreur !== null && (
        <p className="alerte" role="alert">
          {erreur}
        </p>
      )}

      {vue === null ? (
        <p className="etat">Lecture du stock…</p>
      ) : (
        <>
          {/*
            LE CHOIX « UN MAGASIN OU TOUS » (078), demandé par Chris le 15/09/2026.

            Masqué quand il n'y a qu'un magasin : le choix n'existe pas, et l'offrir ferait croire
            qu'il y a autre chose à voir ailleurs. Les chiffres au-dessous suivent ce choix, et
            c'est le même appel — le serveur rend les deux d'un coup, changer de magasin ne
            recharge rien.
          */}
          {vue.sites.length > 1 && (
            <label className="stock-ligne__magasin">
              Magasin
              <select
                value={magasin}
                onChange={(e) => {
                  setMagasin(e.target.value);
                }}
              >
                <option value="">Tous les magasins</option>
                {vue.sites.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.denomination}
                  </option>
                ))}
              </select>
            </label>
          )}

          {/* Le stock ET le disponible : deux nombres pour deux questions. */}
          <dl className={negatif ? "stock-ligne__etat alerte" : "stock-ligne__etat"}>
            <div>
              <dt>En stock{magasin !== "" && vue.sites.length > 1 ? " ici" : ""}</dt>
              <dd>
                {sansZeros(part === null ? vue.etat.quantite : part.quantite)}{" "}
                {vue.article.unite ?? ""}
              </dd>
            </div>
            <div>
              <dt>Déjà réservé</dt>
              <dd>{sansZeros(part === null ? vue.reserveAilleurs : part.reserveAilleurs)}</dd>
            </div>
            <div>
              <dt>Disponible</dt>
              <dd>
                <strong>{sansZeros(part === null ? vue.disponible : part.disponible)}</strong>
              </dd>
            </div>
            <div>
              <dt>Valeur du stock</dt>
              <dd>{part === null ? vue.etat.valeur : part.valeur} €</dd>
            </div>
          </dl>

          {part?.emplacement != null && (
            <p className="stock-ligne__rangement">Rangé en « {part.emplacement} ».</p>
          )}

          {/*
            La répartition, quand on regarde TOUS les magasins.

            Sans elle, « tous » donnerait un total sans dire où la marchandise se trouve — et
            l'assistante choisirait son magasin au hasard avant de réserver. Le disponible y
            figure magasin par magasin, parce que c'est lui qui décide.
          */}
          {magasin === "" && vue.sites.length > 1 && (
            <table className="stock-ligne__magasins">
              <thead>
                <tr>
                  <th>Magasin</th>
                  <th>En stock</th>
                  <th>Disponible</th>
                </tr>
              </thead>
              <tbody>
                {vue.parSite.map((p) => (
                  <tr key={p.siteId ?? "sans-site"}>
                    <td>{p.denomination}</td>
                    <td>{sansZeros(p.quantite)}</td>
                    <td>
                      <strong>{sansZeros(p.disponible)}</strong>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {negatif && (
            <p className="alerte" role="alert">
              Plus a été réservé qu&apos;il n&apos;y a en stock. Ce n&apos;est pas bloqué — il
              manque une entrée, ou une réservation de trop.
            </p>
          )}

          {modifiable && (
            <form
              className="stock-ligne__saisie"
              onSubmit={(e) => {
                e.preventDefault();
                reserver();
              }}
            >
              <label>
                Quantité à réserver
                <input
                  inputMode="decimal"
                  value={quantite}
                  onChange={(e) => {
                    setQuantite(e.target.value);
                  }}
                />
                {quantiteDeLaLigne !== null && (
                  <small>Préremplie avec la quantité chiffrée sur la ligne.</small>
                )}
              </label>

              <label>
                Pour le
                <input
                  type="date"
                  value={pourLe}
                  onChange={(e) => {
                    setPourLe(e.target.value);
                  }}
                />
                <small>La date à laquelle le magasin doit avoir préparé. Facultative.</small>
              </label>

              <label>
                Remarque
                <input
                  value={remarque}
                  onChange={(e) => {
                    setRemarque(e.target.value);
                  }}
                />
              </label>

              {/*
                ON RÉSERVE DANS UN MAGASIN, pas dans « tous ».

                Le bouton le dit plutôt que de rester grisé sans explication : un bouton inerte
                fait chercher ce qui manque dans le formulaire, alors que ce qui manque est en
                haut de l'écran.
              */}
              {magasin === "" && vue.sites.length > 1 && (
                <p className="etat">
                  Choisissez un magasin en haut pour réserver : le magasinier doit savoir dans
                  quel rayonnage aller.
                </p>
              )}
              {vue.sites.length === 0 && (
                <p className="alerte" role="alert">
                  Aucun site de stockage n&apos;est déclaré : rien ne peut être réservé au
                  magasin. À cocher dans Référentiels → Carrières et sites du groupe.
                </p>
              )}

              <button
                type="submit"
                disabled={occupe || quantite.trim() === "" || magasin === ""}
              >
                {occupe
                  ? "Enregistrement…"
                  : part === null || vue.sites.length === 1
                    ? "Réserver au magasin"
                    : `Réserver à ${part.denomination}`}
              </button>
            </form>
          )}

          {avis !== null && <p className="etat">{avis}</p>}

          {vue.reservations.length > 0 && (
            <section className="stock-ligne__reservations">
              <h4>Réservé sur ce chantier</h4>
              <ul>
                {vue.reservations.map((r) => (
                  <li key={r.id} className={`stock-ligne__reservation--${r.statut}`}>
                    <span className="stock-ligne__quantite">
                      {sansZeros(r.quantite)} {r.unite ?? ""}
                    </span>
                    <span>
                      {r.statut === "preparee" ? (
                        <>
                          préparé{r.preparateurNom === null ? "" : ` par ${r.preparateurNom}`}
                          {r.prepareLe !== null && ` le ${formaterDate(r.prepareLe)}`}
                          {/* Une réservation préparée dont le mouvement a disparu : le
                              magasinier l'a supprimé depuis son écran. À dire, jamais à taire —
                              le stock ne porte plus cette sortie. */}
                          {r.mouvementId === null && (
                            <strong className="en-ecart"> · sortie de stock supprimée</strong>
                          )}
                        </>
                      ) : (
                        <>
                          à préparer
                          {r.pourLe !== null && ` pour le ${formaterDateCivile(r.pourLe)}`}
                          {r.demandeurNom === null ? "" : ` · ${r.demandeurNom}`}
                        </>
                      )}
                    </span>
                    {r.remarque !== null && (
                      <span className="stock-ligne__remarque">« {r.remarque} »</span>
                    )}
                    {/* Une réservation préparée ne se retire pas : le stock est sorti. Le geste
                        de correction est alors celui du magasinier, sur son mouvement. */}
                    {modifiable && r.statut === "demandee" && (
                      <button
                        type="button"
                        className="lien"
                        disabled={occupe}
                        onClick={() => {
                          retirer(r);
                        }}
                      >
                        Retirer
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </div>
  );
}
